import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  Tooltip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Grid,
  Stack,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

const UserManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEditPassword, setUserToEditPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [processingUsers, setProcessingUsers] = useState(new Set()); // Track users being processed
  const [areas, setAreas] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    fullName: '',
    email: '',
    role: 'LOAN_OFFICER',
    active: true,
    allAreasAccess: false,
    areaIds: [], // Array of area IDs for multiple selection
    workingTimeStart: '', // Format: HH:mm (e.g., "09:00")
    workingTimeEnd: '',   // Format: HH:mm (e.g., "17:00")
  });
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    console.log('UserManagement: Component mounted, fetching users...');
    fetchUsers();
    fetchAreas();
  }, []);

  const fetchAreas = async () => {
    try {
      setLoadingAreas(true);
      const response = await api.get('/admin/areas/active');
      setAreas(response.data);
    } catch (error) {
      console.error('Error fetching areas:', error);
    } finally {
      setLoadingAreas(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log('UserManagement: Making API call to /admin/users');
      const response = await api.get('/admin/users');
      console.log('UserManagement: API response received:', response);
      const usersList = Array.isArray(response.data) ? response.data : [];
      console.log('UserManagement: Users list:', usersList);
      setUsers(usersList);
      if (usersList.length === 0) {
        toast.info('No users found. Click "Create User" to add a new user.');
      }
    } catch (error) {
      console.error('UserManagement: Error fetching users:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      toast.error(`Error fetching users: ${errorMsg}`);
      // Don't clear users array on error - preserve existing data
      // setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate Field Officer area assignment
      if (formData.role === 'FIELD_OFFICER' && !formData.allAreasAccess && (!formData.areaIds || formData.areaIds.length === 0)) {
        toast.error('Please select at least one area for the Field Officer or grant access to all areas');
        return;
      }
      
      // Prepare submit data
      const submitData = {
        username: formData.username,
        fullName: formData.fullName,
        email: formData.email || null,
        role: formData.role,
        active: formData.active !== undefined ? formData.active : true,
        allAreasAccess: formData.allAreasAccess || false,
        areaIds: formData.role === 'FIELD_OFFICER' && !formData.allAreasAccess && formData.areaIds && formData.areaIds.length > 0
          ? formData.areaIds // Send all selected area IDs
          : [],
        workingTimeStart: formData.workingTimeStart && formData.workingTimeStart.trim() !== '' ? formData.workingTimeStart : null,
        workingTimeEnd: formData.workingTimeEnd && formData.workingTimeEnd.trim() !== '' ? formData.workingTimeEnd : null,
      };
      
      // Only include password if it's provided (for updates) or required (for creates)
      if (formData.password && formData.password.trim().length > 0) {
        submitData.password = formData.password;
      } else if (!editingUser) {
        // Password is required for new users
        toast.error('Password is required for new users');
        return;
      }
      
      let response;
      if (editingUser) {
        // Update existing user
        response = await api.put(`/admin/users/${editingUser.id}`, submitData);
        toast.success('User updated successfully!');
      } else {
        // Create new user
        response = await api.post('/admin/users', submitData);
        toast.success('User created successfully!');
      }
      
      const updatedUser = response.data;
      
      // Update user in list with the response data
      if (editingUser) {
        setUsers(prevUsers => prevUsers.map(user => 
          user.id === editingUser.id ? updatedUser : user
        ));
      } else {
        setUsers(prevUsers => [...prevUsers, updatedUser]);
      }
      
      setOpen(false);
      setEditingUser(null);
      setFormData({ 
        username: '', 
        password: '', 
        fullName: '', 
        email: '', 
        role: 'LOAN_OFFICER',
        active: true,
        allAreasAccess: false,
        areaIds: [],
        workingTimeStart: '',
        workingTimeEnd: '',
      });
      
      // Refresh users list after a short delay to ensure we have the latest data
      // This helps ensure area assignments and other relationships are properly loaded
      setTimeout(() => {
        fetchUsers().catch(err => {
          // If refresh fails, don't clear users - keep the updated state
          console.error('Error refreshing users after update:', err);
        });
      }, 500);
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      
      // Special handling for admin creation permission errors
      if (errorMessage && errorMessage.includes('Only administrators can')) {
        toast.error('Permission Denied: ' + errorMessage);
      } else {
        toast.error('Error saving user: ' + errorMessage);
      }
    }
  };

  const handleActivate = async (userId) => {
    try {
      setProcessingUsers(prev => new Set(prev).add(userId));
      
      const response = await api.post(`/admin/users/${userId}/activate`);
      const updatedUser = response.data;
      
      // Update user status in real-time using API response
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === userId ? { ...user, ...updatedUser } : user
      ));
      
      toast.success('User activated successfully!');
    } catch (error) {
      console.error('Error activating user:', error);
      toast.error('Error activating user: ' + (error.response?.data?.message || error.message));
      
      // Refresh users list to ensure consistency
      fetchUsers();
    } finally {
      setProcessingUsers(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleDeactivate = async (userId) => {
    try {
      setProcessingUsers(prev => new Set(prev).add(userId));
      
      const response = await api.post(`/admin/users/${userId}/deactivate`);
      const updatedUser = response.data;
      
      // Update user status in real-time using API response
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === userId ? { ...user, ...updatedUser } : user
      ));
      
      toast.success('User deactivated successfully!');
    } catch (error) {
      console.error('Error deactivating user:', error);
      toast.error('Error deactivating user: ' + (error.response?.data?.message || error.message));
      
      // Refresh users list to ensure consistency
      fetchUsers();
    } finally {
      setProcessingUsers(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    const userId = userToDelete.id;
    
    try {
      setProcessingUsers(prev => new Set(prev).add(userId));
      
      const response = await api.delete(`/admin/users/${userId}`);
      
      // Remove user from list in real-time
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      
      toast.success('User deleted successfully!');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to delete user';
      toast.error('Error deleting user: ' + errorMessage);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      
      // Refresh users list to ensure consistency
      fetchUsers().catch(err => {
        console.error('Error refreshing users after delete failure:', err);
      });
    } finally {
      setProcessingUsers(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleEditPasswordClick = (user) => {
    setUserToEditPassword(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordDialogOpen(true);
  };

  const handlePasswordUpdate = async () => {
    if (!userToEditPassword) return;
    
    // Validate passwords
    if (!newPassword || newPassword.trim().length === 0) {
      toast.error('Password cannot be empty');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    const userId = userToEditPassword.id;
    
    try {
      setProcessingUsers(prev => new Set(prev).add(userId));
      
      await api.put(`/admin/users/${userId}/password?newPassword=${encodeURIComponent(newPassword)}`);
      
      toast.success('Password updated successfully!');
      setPasswordDialogOpen(false);
      setUserToEditPassword(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Error updating password: ' + (error.response?.data?.message || error.message));
    } finally {
      setProcessingUsers(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  // No filtering - display all users

  const getRoleColor = (role) => {
    // Normalize role for color determination
    const normalizedRole = normalizeRole(role);
    switch (normalizedRole) {
      case 'ADMIN':
        return 'error';
      case 'LOAN_OFFICER':
        return 'primary';
      case 'FIELD_OFFICER':
        return 'success';
      default:
        return 'default';
    }
  };
  
  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'Admin';
      case 'LOAN_OFFICER':
        return 'Loan Officer';
      case 'FIELD_OFFICER':
        return 'Field Officer';
      case 'COLLECTOR':
        return 'Field Officer (Legacy)';
      case 'OFFICER':
        return 'Loan Officer (Legacy)';
      default:
        return role;
    }
  };
  
  // Convert old role names to new role names
  const normalizeRole = (role) => {
    if (role === 'COLLECTOR') {
      return 'FIELD_OFFICER';
    } else if (role === 'OFFICER') {
      return 'LOAN_OFFICER';
    }
    return role;
  };
  
  const handleEditUser = (user) => {
    setEditingUser(user);
    // Normalize role: convert old role names to new ones
    const normalizedRole = normalizeRole(user.role || 'LOAN_OFFICER');
    // Get all area IDs if assigned areas exist (multiple area selection)
    const areaIds = user.assignedAreas && user.assignedAreas.length > 0 
      ? user.assignedAreas.map(area => area.id || area)
      : [];
    setFormData({
      username: user.username || '',
      password: '',
      fullName: user.fullName || '',
      email: user.email || '',
      role: normalizedRole,
      active: user.active !== undefined ? user.active : true,
      allAreasAccess: user.allAreasAccess || false,
      areaIds: areaIds,
      workingTimeStart: user.workingTimeStart || '',
      workingTimeEnd: user.workingTimeEnd || '',
    });
    setOpen(true);
  };

  const activeUsersCount = users.filter(u => u.active).length;
  const inactiveUsersCount = users.filter(u => !u.active).length;

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          mb: 3,
          gap: 2,
        }}
      >
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            color: '#0D47A1',
            fontSize: { xs: '1.75rem', sm: '2rem' },
          }}
        >
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button 
            variant="outlined" 
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            disabled={loading}
            sx={{
              borderColor: '#0D47A1',
              color: '#0D47A1',
              '&:hover': {
                borderColor: '#0D47A1',
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
              },
            }}
          >
            Refresh
          </Button>
        <Button 
          variant="contained" 
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingUser(null);
            setFormData({ 
              username: '', 
              password: '', 
              fullName: '', 
              email: '', 
              role: 'LOAN_OFFICER',
              active: true,
              allAreasAccess: false,
              areaIds: [],
            });
            setOpen(true);
          }}
            sx={{ 
              minWidth: { xs: '100%', sm: 150 },
              background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
              boxShadow: '0 4px 15px rgba(13, 71, 161, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565C0 0%, #0A3D91 100%)',
                boxShadow: '0 6px 20px rgba(13, 71, 161, 0.5)',
              },
            }}
        >
          Create User
        </Button>
        </Box>
      </Box>


      {/* Summary Cards */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          mb: 3 
        }}
      >
        <Paper 
          elevation={3}
          sx={{ 
            p: 2, 
            flex: 1, 
            background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)', 
            color: 'white',
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(13, 71, 161, 0.4)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Total Users</Typography>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>{users.length}</Typography>
        </Paper>
        <Paper 
          elevation={3}
          sx={{ 
            p: 2, 
            flex: 1, 
            background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)', 
            color: 'white',
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(46, 125, 50, 0.3)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Active Users</Typography>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>{activeUsersCount}</Typography>
        </Paper>
        <Paper 
          elevation={3}
          sx={{ 
            p: 2, 
            flex: 1, 
            background: 'linear-gradient(135deg, #616161 0%, #424242 100%)', 
            color: 'white',
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(97, 97, 97, 0.3)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Inactive Users</Typography>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>{inactiveUsersCount}</Typography>
        </Paper>
      </Box>
      
      {/* Mobile Card View */}
      {isMobile && (
        <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 3 }}>
          {loading ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <CircularProgress sx={{ mr: 2 }} />
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Loading users...
              </Typography>
            </Paper>
          ) : users.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                No users found. Click "Create User" to add a new user.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2}>
              {users.map((user) => (
                <Card
                  key={user.id}
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    backgroundColor: user.active ? 'inherit' : 'rgba(0, 0, 0, 0.02)',
                    opacity: user.active ? 1 : 0.7,
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(13, 71, 161, 0.2)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                          Username
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                          {user.username}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={user.active ? 'Active' : 'Inactive'}
                          color={user.active ? 'success' : 'default'}
                          size="small"
                          icon={user.active ? <CheckCircleIcon sx={{ fontSize: '0.875rem !important' }} /> : <CancelIcon sx={{ fontSize: '0.875rem !important' }} />}
                          sx={{ fontSize: '0.65rem', height: 24 }}
                        />
                        <Chip
                          label={getRoleDisplayName(user.role)}
                          color={getRoleColor(user.role)}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: 24 }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                        Full Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                        {user.fullName || 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {user.email || 'N/A'}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                          User ID
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          #{user.id}
                        </Typography>
                      </Box>
                      {user.createdAt && (
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                            Created Date
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {new Date(user.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {user.workingTimeStart && user.workingTimeEnd && (
                      <Box sx={{ mb: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                          Working Hours
                        </Typography>
                        <Chip
                          label={`${user.workingTimeStart} - ${user.workingTimeEnd} (SLT)`}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </Box>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                      <Tooltip title="Edit User">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditUser(user)}
                          disabled={processingUsers.has(user.id)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {user.role !== 'ADMIN' && (
                        <>
                          {user.active ? (
                            <Tooltip title="Deactivate User">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleDeactivate(user.id)}
                                disabled={processingUsers.has(user.id)}
                              >
                                {processingUsers.has(user.id) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <CancelIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Activate User">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleActivate(user.id)}
                                disabled={processingUsers.has(user.id)}
                              >
                                {processingUsers.has(user.id) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <CheckCircleIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                      <Tooltip title="Edit Password">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleEditPasswordClick(user)}
                          disabled={processingUsers.has(user.id)}
                        >
                          <LockIcon />
                        </IconButton>
                      </Tooltip>
                      {user.role !== 'ADMIN' && (
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(user)}
                            disabled={processingUsers.has(user.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* Desktop/Tablet Table View */}
      <TableContainer 
        component={Paper} 
        elevation={3}
        sx={{
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: { xs: 'none', sm: 'block' },
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#0D47A1',
            borderRadius: 4,
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Username</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Full Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Role</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Working Hours</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Created Date</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <CircularProgress sx={{ mr: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading users...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    No users found. Click "Create User" to add a new user.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow 
                  key={user.id}
                  sx={{ 
                    backgroundColor: user.active ? 'inherit' : 'rgba(0, 0, 0, 0.02)',
                    opacity: user.active ? 1 : 0.7,
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.05)',
                    }
                  }}
                >
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>{user.id}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}><strong>{user.username}</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>{user.fullName || 'N/A'}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, display: { xs: 'none', md: 'table-cell' } }}>{user.email || 'N/A'}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                    <Chip 
                      label={getRoleDisplayName(user.role)} 
                      color={getRoleColor(user.role)}
                      size="small"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                    <Chip
                      label={user.active ? 'Active' : 'Inactive'}
                      color={user.active ? 'success' : 'default'}
                      size="small"
                      icon={user.active ? <CheckCircleIcon /> : <CancelIcon />}
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, display: { xs: 'none', lg: 'table-cell' } }}>
                    {user.workingTimeStart && user.workingTimeEnd ? (
                      <Tooltip title={`User can access system from ${user.workingTimeStart} to ${user.workingTimeEnd} daily (Sri Lankan time)`}>
                        <Chip
                          label={`${user.workingTimeStart} - ${user.workingTimeEnd} (SLT)`}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        24/7 Access
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, display: { xs: 'none', lg: 'table-cell' } }}>
                    {user.createdAt 
                      ? new Date(user.createdAt).toLocaleDateString() 
                      : 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      {/* Edit button */}
                      <Tooltip title="Edit User">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditUser(user)}
                          disabled={processingUsers.has(user.id)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {/* Only show activate/deactivate for non-admin users */}
                      {user.role !== 'ADMIN' && (
                        <>
                      {user.active ? (
                        <Tooltip title="Deactivate User">
                              <span>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleDeactivate(user.id)}
                                  disabled={processingUsers.has(user.id)}
                          >
                                  {processingUsers.has(user.id) ? (
                                    <CircularProgress size={20} />
                                  ) : (
                            <CancelIcon />
                                  )}
                          </IconButton>
                              </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate User">
                              <span>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleActivate(user.id)}
                                  disabled={processingUsers.has(user.id)}
                          >
                                  {processingUsers.has(user.id) ? (
                                    <CircularProgress size={20} />
                                  ) : (
                            <CheckCircleIcon />
                                  )}
                          </IconButton>
                              </span>
                        </Tooltip>
                          )}
                        </>
                      )}
                      <Tooltip title="Edit Password">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleEditPasswordClick(user)}
                          disabled={processingUsers.has(user.id)}
                        >
                          <LockIcon />
                        </IconButton>
                      </Tooltip>
                      {/* Only show delete for non-admin users */}
                      {user.role !== 'ADMIN' && (
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(user)}
                            disabled={processingUsers.has(user.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create User Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' },
          },
        }}
      >
        <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Username"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            margin="normal"
            required
            autoFocus
            disabled={!!editingUser}
          />
          <TextField
            fullWidth
            label={editingUser ? "New Password (leave blank to keep current)" : "Password"}
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            margin="normal"
            required={!editingUser}
          />
          <TextField
            fullWidth
            label="Full Name"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            select
            label="Role"
            value={formData.role}
            onChange={(e) => {
              const newRole = e.target.value;
              setFormData({ 
                ...formData, 
                role: newRole,
                // Reset area assignments when role changes
                allAreasAccess: false,
                areaIds: [],
              });
            }}
            margin="normal"
            required
            helperText={formData.role === 'ADMIN' ? 'Note: Only existing administrators can create new administrator accounts.' : ''}
          >
            <MenuItem value="ADMIN">Admin</MenuItem>
            <MenuItem value="LOAN_OFFICER">Loan Officer</MenuItem>
            <MenuItem value="FIELD_OFFICER">Field Officer</MenuItem>
          </TextField>
          
          {/* Area Assignment for Field Officers */}
          {formData.role === 'FIELD_OFFICER' && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Area Assignment
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.allAreasAccess}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      allAreasAccess: e.target.checked,
                      areaIds: e.target.checked ? [] : formData.areaIds,
                    })}
                  />
                }
                label="Access to All Areas"
              />
              {!formData.allAreasAccess && (
                <FormGroup sx={{ mt: 1, maxHeight: 200, overflowY: 'auto' }}>
                  {loadingAreas ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Loading areas...
                      </Typography>
                    </Box>
                  ) : areas.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No areas available. Please create areas first.
                    </Typography>
                  ) : (
                    areas.map((area) => (
                      <FormControlLabel
                        key={area.id}
                        control={
                          <Checkbox
                            checked={formData.areaIds.includes(area.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  areaIds: [...formData.areaIds, area.id],
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  areaIds: formData.areaIds.filter(id => id !== area.id),
                                });
                              }
                            }}
                          />
                        }
                        label={area.name}
                      />
                    ))
                  )}
                </FormGroup>
              )}
            </Box>
          )}

          {/* Working Time Assignment */}
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Working Time Period (Optional)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Set the time period when this user can access the system. Times are based on <strong>Sri Lankan time (Asia/Colombo)</strong>. Leave empty for 24/7 access.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Time (Sri Lankan Time)"
                  type="time"
                  value={formData.workingTimeStart}
                  onChange={(e) => setFormData({ ...formData, workingTimeStart: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText="User can access system from this time (Sri Lankan time)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Time (Sri Lankan Time)"
                  type="time"
                  value={formData.workingTimeEnd}
                  onChange={(e) => setFormData({ ...formData, workingTimeEnd: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText="User can access system until this time (Sri Lankan time)"
                />
              </Grid>
            </Grid>
            {(formData.workingTimeStart || formData.workingTimeEnd) && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Note:</strong> If both times are set, the user will only be able to log in and access the system during this period each day (based on Sri Lankan time). 
                  The user can log in at any time within the specified time range. Outside this period, access will be automatically denied.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpen(false);
            setEditingUser(null);
            setFormData({ 
              username: '', 
              password: '', 
              fullName: '', 
              email: '', 
              role: 'LOAN_OFFICER',
              active: true,
              allAreasAccess: false,
              areaIds: [],
              workingTimeStart: '',
              workingTimeEnd: '',
            });
          }}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={
              !formData.username || 
              (!editingUser && !formData.password) || 
              !formData.fullName ||
              (formData.role === 'FIELD_OFFICER' && !formData.allAreasAccess && (!formData.areaIds || formData.areaIds.length === 0))
            }
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Warning:</strong> This action cannot be undone. Deleting a user will permanently remove their account.
          </Alert>
          {userToDelete && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Username:</strong> {userToDelete.username}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Full Name:</strong> {userToDelete.fullName}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Role:</strong> {userToDelete.role}
              </Typography>
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                Are you sure you want to delete this user?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setUserToDelete(null);
            }}
            disabled={processingUsers.has(userToDelete?.id)}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={processingUsers.has(userToDelete?.id)}
          >
            {processingUsers.has(userToDelete?.id) ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Password</DialogTitle>
        <DialogContent>
          {userToEditPassword && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Changing password for: <strong>{userToEditPassword.username}</strong> ({userToEditPassword.fullName})
              </Typography>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                required
                autoFocus
                helperText="Password must be at least 6 characters long"
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                error={confirmPassword !== '' && newPassword !== confirmPassword}
                helperText={confirmPassword !== '' && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPasswordDialogOpen(false);
            setUserToEditPassword(null);
            setNewPassword('');
            setConfirmPassword('');
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handlePasswordUpdate} 
            variant="contained"
            color="primary"
            disabled={
              !newPassword || 
              newPassword.length < 6 || 
              newPassword !== confirmPassword ||
              processingUsers.has(userToEditPassword?.id)
            }
          >
            {processingUsers.has(userToEditPassword?.id) ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Updating...
              </>
            ) : (
              'Update Password'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
